import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import EmptyState from '@/components/EmptyState';
import Select from '@/components/Select';
import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IParentScreenTimeGraphItem } from '@/types/parent';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IScreenTimeSummaryChartProps {
  data: IParentScreenTimeGraphItem[];
  periodFilter: string;
  onPeriodChange: (val: string) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CHART_FONT_FAMILY = 'Aptos, sans-serif';
const BAR_COLOR = '#4338CA';

// ─── Component ────────────────────────────────────────────────────────────────

const ScreenTimeSummaryChart: React.FC<IScreenTimeSummaryChartProps> = ({
  data,
  periodFilter,
  onPeriodChange,
}) => {
  const { formatText } = useFormatText();
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const periodOptions = [
    {
      value: 'this-week',
      label: formatText('parent-controls.filter.this-week'),
    },
    {
      value: 'last-week',
      label: formatText('parent-controls.filter.last-week'),
    },
    {
      value: 'this-month',
      label: formatText('parent-controls.filter.this-month'),
    },
    {
      value: 'last-month',
      label: formatText('parent-controls.filter.last-month'),
    },
  ];

  const chartData = useMemo(
    () =>
      (data ?? []).map((item) => ({
        label: item?.label ?? '',
        hours: Math.round(((item?.duration_seconds ?? 0) / 3600) * 10) / 10,
      })),
    [data]
  );

  const hasActivity = chartData.some((item) => (item?.hours ?? 0) > 0);

  const yMax = useMemo(() => {
    const maxHours = chartData.reduce(
      (acc, item) => Math.max(acc, item?.hours ?? 0),
      0
    );
    if (maxHours <= 0) return 5;
    return Math.max(5, Math.ceil(maxHours));
  }, [chartData]);

  const series = useMemo(
    () => [
      {
        name: 'Hours',
        data: chartData.map((item) => item?.hours ?? 0),
      },
    ],
    [chartData]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 203,
        toolbar: { show: false },
        fontFamily: CHART_FONT_FAMILY,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      fill: {
        opacity: 1,
        colors: [BAR_COLOR],
      },
      colors: [BAR_COLOR],
      xaxis: {
        categories: chartData?.map((item) => item.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 5,
        opposite: isRTL,
        labels: {
          formatter: (val) => `${val} hr`,
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 16, bottom: 0, left: 16 },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const day = w.globals.labels[dataPointIndex] as string;
          const hours = series[seriesIndex][dataPointIndex] as number;
          const value = `${hours} hr`;

          return `
            <div style="
              background: white;
              border: 1px solid #f5f7fa;
              border-radius: 5px;
              box-shadow: 0 0 18px 0 rgba(38,38,38,0.2);
              padding: 10px 11px;
              display: flex;
              flex-direction: column;
              gap: 6px;
              min-width: 80px;
            ">
              <span style="
                font-family: Aptos, sans-serif;
                font-size: 12px;
                font-weight: 600;
                line-height: 20px;
                color: #4f46e5;
                white-space: nowrap;
              ">${day}</span>
              <div style="display: flex; gap: 12px; align-items: center;">
                <span style="
                  font-family: Aptos, sans-serif;
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 16px;
                  color: #525866;
                  white-space: nowrap;
                ">Time</span>
                <span style="
                  font-family: Aptos, sans-serif;
                  font-size: 12px;
                  font-weight: 600;
                  line-height: 20px;
                  color: #181b25;
                  white-space: nowrap;
                ">${value}</span>
              </div>
            </div>
          `;
        },
      },
      legend: { show: false },
    }),
    [chartData, isRTL, yMax]
  );

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Chart header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-neutral-primary leading-6">
          {formatText('parent-controls.screen-time.summary-title')}
        </h3>
        <div className="w-36">
          <Select
            options={periodOptions}
            selected={periodFilter}
            onChange={(val) => onPeriodChange(val as string)}
          />
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1">
        {hasActivity ? (
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={203}
          />
        ) : (
          <EmptyState
            imageSrc="/not-found.svg"
            imageAlt="no-data"
            title={formatText('parent-controls.screen-time.empty-title')}
            description={formatText(
              'parent-controls.screen-time.empty-description'
            )}
            width={120}
            height={120}
            className="h-auto! min-h-50! gap-3! py-4!"
            titleClassName="text-base!"
            dataTestId="screen-time-summary-empty"
          />
        )}
      </div>
    </div>
  );
};

export default ScreenTimeSummaryChart;
